/**
 * Overflow menu for a single unit row. Lazy-loads the unit's row options when the row
 * renders (i.e. when the property card is expanded), builds the menu entirely from that
 * data, and disables the menu trigger while the request is in flight.
 */
import { useRouter } from 'next/router';

import { AccountViewListingOptionType } from '@zg-rentals/ts-rental-manager-api-client';

import { usePropertyTableUnitOptions } from '../../../hooks/queries/propertyTable';

import { buildOverflowMenuItems, OverflowMenu } from './OverflowMenu';

import type { ListingAlias, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingAlias: ListingAlias;
  unitNumber: string;
  address: RentalManagerAddressDto;
  onArchiveClick: (listingAlias: ListingAlias, address: RentalManagerAddressDto) => void;
};

const UnitOverflowMenu = ({ listingAlias, unitNumber, address, onArchiveClick }: Props) => {
  const router = useRouter();
  const { data, isFetching } = usePropertyTableUnitOptions(listingAlias);

  const items = buildOverflowMenuItems(data?.options ?? [], {
    router,
    actionOverrides: {
      // Archive opens a modal rather than navigating.
      [AccountViewListingOptionType.ARCHIVE]: () => onArchiveClick(listingAlias, address),
      // TODO: add an ADD_RENT handler here to launch the Add Rent modal.
    },
  });

  return (
    <OverflowMenu
      triggerLabel={unitNumber ? `Options for ${unitNumber}` : 'Unit options'}
      items={items}
      disabled={isFetching}
    />
  );
};

export default UnitOverflowMenu;
